/**
 * Компонент редактирования критерия фильтрации
 * Объединяет FieldSelector, OperatorSelector и ValueInput
 */

import { observer } from "mobx-react-lite";
import * as React from "react";

import { QueryBuilderStore } from "../../QueryBuilderStore";
import {
  ZCriterionNode,
  ZFilterableField,
  ZFilterValue,
  ZNodePath,
} from "../../types";
import { FieldSelector } from "../FieldSelector/FieldSelector";
import { OperatorSelector } from "../OperatorSelector/OperatorSelector";
import { ValueInput } from "../ValueInput/ValueInput";
import * as styles from "./CriterionEditor.module.less";

interface PropsCriterionEditor {
  /** Критерий для редактирования */
  criterion: ZCriterionNode;

  /** Путь к критерию */
  path: ZNodePath;

  /** Store для доступа к метаданным */
  store: QueryBuilderStore;
}

/**
 * Полный редактор критерия с выбором поля, оператора и значения
 */
export const CriterionEditor: React.FC<PropsCriterionEditor> = observer(
  ({ criterion, path, store }) => {
    const showValidationErrors = store.draftStore.shouldShowValidationErrors;

    const handleFieldChange = (
      _: string | null,
      field: ZFilterableField | undefined,
    ) => {
      store.selectCriterionField(path, field ?? null);
    };

    const handleOperatorChange = (operator: string | null) => {
      store.selectCriterionOperator(path, operator);
    };

    const handleValueChange = (value: ZFilterValue) => {
      store.setCriterionValue(path, value);
    };

    const currentField = criterion.fieldMeta
      ? store.availableFields.find(
          (f) => f.fieldMeta.field === criterion.fieldMeta?.field,
        )
      : undefined;

    // Получение доступных операторов
    const availableOperators = store.getOperatorsForValueType(
      criterion.valueType,
    );

    // Проверка, требует ли текущий оператор значения
    const requiresValue =
      criterion.operator && criterion.valueType
        ? store.doesOperatorRequireValue(
            criterion.operator,
            criterion.valueType,
          )
        : true;

    const hasFieldError = showValidationErrors && !criterion.fieldMeta;
    const hasOperatorError = showValidationErrors && !criterion.operator;
    const hasValueError =
      showValidationErrors &&
      store.draftStore.shouldValidateCriterionValue &&
      requiresValue &&
      (criterion.value === null ||
        criterion.value === undefined ||
        criterion.value === "");

    const requiredFieldErrorText = "Необходимо заполнить поле";

    return (
      <div className={styles.criterionEditor}>
        <FieldSelector
          fields={store.availableFields}
          value={currentField?.tag || null}
          onChange={handleFieldChange}
          errorText={hasFieldError ? requiredFieldErrorText : undefined}
        />

        <OperatorSelector
          operators={availableOperators}
          value={criterion.operator}
          onChange={handleOperatorChange}
          disabled={!criterion.fieldMeta || availableOperators.length === 0}
          errorText={hasOperatorError ? requiredFieldErrorText : undefined}
        />

        <ValueInput
          valueType={criterion.valueType}
          value={criterion.value}
          onChange={handleValueChange}
          disabled={!criterion.operator}
          requiresValue={requiresValue}
          errorText={hasValueError ? requiredFieldErrorText : undefined}
        />
      </div>
    );
  },
);
